import {
  BulkActionContainerCol,
  Col,
  Table,
  TableActionBarRow,
  ValueOrFallback,
} from "melco-ui";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  BulkActionCount,
  FormattedDate,
  EfsMelcoEfsModelsMappingListItem,
  Search,
  useTableSelection,
  TableCellLink,
} from "melco-shared-logic";
import { useDesignMappings } from "../../hooks/designMappings/useDesignMappings";
import { BulkActionDeleteDesignMappings } from "./BulkActionDeleteDesignMappings";

const DETAIL_VIEW_PATH = "/design-mappings";

export const DesignMappingsList: React.FC = () => {
  const {
    isLoading,
    isFetching,
    data,
    isPreviousData,
    errorComponent,
    onTableConfigChange,
    pagination,
    searchQueryChange,
    refetch,
  } = useDesignMappings();

  const navigate = useNavigate();

  const { t } = useTranslation();

  const { selectedRowKeys, setSelectedRowKeys, rowSelection, onRow } =
    useTableSelection((row: EfsMelcoEfsModelsMappingListItem) => {
      navigate([DETAIL_VIEW_PATH, row.id].join("/"));
    });

  const columns = [
    {
      title: t("design_mappings_list.columns.name"),
      key: "name",
      dataIndex: "name",
      sorter: true,
      render: (_: string, item: EfsMelcoEfsModelsMappingListItem) => (
        <ValueOrFallback
          value={item.name}
          render={(value) => (
            <TableCellLink to={[DETAIL_VIEW_PATH, item.id].join("/")}>
              {value}
            </TableCellLink>
          )}
          fallback={t("global.fallback.empty")}
        />
      ),
    },
    {
      title: t("design_mappings_list.columns.format_name"),
      key: "format_name",
      dataIndex: "format_name",
      sorter: true,
      render: (formatName: string) => (
        <ValueOrFallback
          value={formatName}
          fallback={t("global.fallback.empty")}
        />
      ),
    },
    {
      title: t("design_mappings_list.columns.format_type"),
      key: "format_key",
      dataIndex: "format_key",
      sorter: true,
    },
    {
      title: t("design_mappings_list.columns.design_name"),
      key: "design_name",
      dataIndex: "design_name",
      sorter: true,
    },
    {
      title: t("design_mappings_list.columns.mapping_condition_count"),
      key: "mapping_condition_count",
      dataIndex: "mapping_condition_count",
      sorter: true,
    },
    {
      title: t("design_mappings_list.columns.created_date"),
      key: "created_date",
      dataIndex: "created_date",
      sorter: true,
      render: (_: string, item: EfsMelcoEfsModelsMappingListItem) => (
        <FormattedDate date={item.created_date} />
      ),
    },
    {
      title: t("design_mappings_list.columns.updated_date"),
      key: "updated_date",
      dataIndex: "updated_date",
      sorter: true,
      render: (_: string, item: EfsMelcoEfsModelsMappingListItem) => (
        <FormattedDate date={item.updated_date} />
      ),
    },
  ];

  return (
    <>
      <TableActionBarRow>
        <Col>
          <Search
            onSearchInputChange={(query: string) => searchQueryChange(query)}
          />
        </Col>
        <BulkActionContainerCol>
          <BulkActionCount selectedCount={selectedRowKeys.length} />
          <BulkActionDeleteDesignMappings
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
            refetch={refetch}
          />
        </BulkActionContainerCol>
      </TableActionBarRow>
      <Table
        showSorterTooltip={false}
        sticky
        loading={isPreviousData ? isFetching : isLoading}
        dataSource={data}
        columns={columns}
        onChange={onTableConfigChange}
        pagination={pagination}
        rowKey="id"
        rowSelection={rowSelection}
        onRow={onRow}
        rowClassName={() => "clickable"}
        noDataMessage={t("design_mappings_list.empty")}
        errorComponent={errorComponent}
      />
    </>
  );
};
